import * as _ from 'lodash';
import * as moment from 'moment';
import * as angular from 'angular';
import { getMomentFromHippoDateString } from 'common/source/utilities/dates';
import { DERIVED_PAYMENT_STATUS, getDerivedPaymentStatus } from 'utilities/paymentsHelper';

interface PolicyRelation {
    policyInfo: CustomerPolicyInfo;
    renewalCustomerPolicyInfo?: CustomerPolicyInfo;
    previousCustomerPolicyInfo?: CustomerPolicyInfo;
}

type PolicyRelations = { [policyId: string]: PolicyRelation };

const greenPaymentStatus = [DERIVED_PAYMENT_STATUS.PAID, DERIVED_PAYMENT_STATUS.UPCOMING_PAYMENT];

function findCustomerPolicyInfoById(policyInfos: Array<CustomerPolicyInfo>, policyId: PolicyId) {
    return _.find(policyInfos, (policyInfo) => {
        return policyInfo.id === policyId;
    });
}

export function getPolicyRelations(policyInfos: Array<CustomerPolicyInfo>) {
    const policyRelations: PolicyRelations = {};

    _.each(policyInfos, (policyInfo) => {
        policyRelations[policyInfo.id] = {
            policyInfo: policyInfo
        };
    });

    _.each(policyInfos, (policyInfo) => {
        const policyRelation = policyRelations[policyInfo.id];

        if (policyInfo.previous_policy) {
            const previousCustomerPolicyInfo: CustomerPolicyInfo | undefined = findCustomerPolicyInfoById(
                policyInfos,
                policyInfo.previous_policy.policy_id
            );

            if (!previousCustomerPolicyInfo) {
                return;
            }

            const previousPolicyRelation = policyRelations[previousCustomerPolicyInfo.id];

            // set the PREVIOUS policyInfo of the CURRENT policy
            policyRelation.previousCustomerPolicyInfo = previousCustomerPolicyInfo;

            // set the RENEWAL policyInfo of the PREVIOUS policy
            previousPolicyRelation.renewalCustomerPolicyInfo = policyInfo;
        }
    });

    return policyRelations;
}

function isHistoricalPolicy(policyInfo: CustomerPolicyInfo, policyRelation: PolicyRelation): boolean {
    return (
        isInactive(policyInfo) &&
        (isValidPreviousPolicy(policyInfo, policyRelation) || wasInactiveMoreThan30Days(policyInfo))
    );
}

// Filter out the following policies:
// 1. Policy with status 'pending_active' and is an upcoming renewal policy (i.e. previous_policy is active).
// 2. Policy with status 'cancelled' or 'expired' that ended greater than thirty days ago
// 3. Previous policy that has renewal policy that is active
export function getActionableCustomerPolicyInfos(
    policyInfos: Array<CustomerPolicyInfoWithIndex>
): Array<CustomerPolicyInfoWithIndex> {
    const policyRelations = getPolicyRelations(policyInfos);
    return policyInfos.filter((policyInfo: CustomerPolicyInfo) => {
        const policyRelation = policyRelations[policyInfo.id];
        const isPendingActiveRenewal = isValidPendingRenewalPolicy(policyInfo, policyRelation);
        const historicalPolicy = isHistoricalPolicy(policyInfo, policyRelation);
        const wasOnlyActiveForOneDay = isInactive(policyInfo) && !wasActiveAtLeastOneDay(policyInfo);
        return !isPendingActiveRenewal && !historicalPolicy && !wasOnlyActiveForOneDay;
    });
}

// Only return following policies:
// 1. Policy with status 'cancelled' or 'expired' and is disjointed from upcoming renewal policy
// (i.e. renewal_policy is 'pending_active').
// 2. Policy with status 'cancelled' or 'expired' that ended greater than thirty days ago
// 3. Previous policy that has renewal policy that is active
export function getHistoricalCustomerPolicyInfos(
    policyInfos: Array<CustomerPolicyInfoWithIndex>
): Array<CustomerPolicyInfoWithIndex> {
    const policyRelations = getPolicyRelations(policyInfos);
    return policyInfos.filter((policyInfo: CustomerPolicyInfo) => {
        const policyRelation = policyRelations[policyInfo.id];
        return isHistoricalPolicy(policyInfo, policyRelation) && wasActiveAtLeastOneDay(policyInfo);
    });
}

export function isActive(policyInfo: CustomerPolicyInfo) {
    return !isInactive(policyInfo);
}

export function isInactive(policyInfo: CustomerPolicyInfo) {
    return policyInfo.status === 'terminated' || policyInfo.status === 'expired';
}

export function wasInactive30DaysOrLess(policyInfo: CustomerPolicyInfo) {
    const policyEndDate =
        policyInfo.status === 'terminated'
            ? moment(policyInfo.update_date)
            : getMomentFromHippoDateString(policyInfo.expiration_date);
    return policyEndDate.isAfter(moment().subtract(30, 'day'));
}

export function wasInactiveMoreThan30Days(policyInfo: CustomerPolicyInfo) {
    return !wasInactive30DaysOrLess(policyInfo);
}

export function wasActiveAtLeastOneDay(policyInfo: CustomerPolicyInfo) {
    const effectiveDate = getMomentFromHippoDateString(policyInfo.effective_date);
    const endDate =
        policyInfo.status === 'terminated'
            ? moment(policyInfo.update_date)
            : getMomentFromHippoDateString(policyInfo.expiration_date);

    return endDate.isSameOrAfter(effectiveDate.add(1, 'day'));
}

export function isValidPendingRenewalPolicy(policyInfo: CustomerPolicyInfo, policyRelation: PolicyRelation): boolean {
    if (
        policyInfo.status === 'pending_active' &&
        policyInfo.previous_policy &&
        policyRelation.previousCustomerPolicyInfo
    ) {
        return policyRelation.previousCustomerPolicyInfo.status === 'active';
        // To handle if a policy is being renewed but previous policy
        // is in cancelled or expired state before renewal goes active
    }
    return false;
}

export function isValidPreviousPolicy(policyInfo: CustomerPolicyInfo, policyRelation: PolicyRelation): boolean {
    let hasActivePolicyRenewal = false;
    const renewalPolicyInfo = policyRelation.renewalCustomerPolicyInfo;
    if (renewalPolicyInfo) {
        hasActivePolicyRenewal =
            renewalPolicyInfo.status === 'active' ||
            (renewalPolicyInfo.status === 'pending_active' && isInactive(policyInfo));
        // To handle if a policy is being renewed but previous policy
        // is in cancelled or expired state before renewal goes active
    }

    return !!(isInactive(policyInfo) && hasActivePolicyRenewal);
}

export function getSortedCustomerPolicyInfos(policyInfos: Array<CustomerPolicyInfo>): Array<CustomerPolicyInfo> {
    // Need this so that we don't need to depend on pod to provide
    // policies with the same effective
    // sorting order: paid status (paid, upcoming payment to bottom), effective date, id
    const linearOrderedPolicyInfos = _.orderBy(
        policyInfos,
        [
            (policyInfo: CustomerPolicyInfo) => {
                const status = getDerivedPaymentStatus(policyInfo);
                return status === DERIVED_PAYMENT_STATUS.PAID || status === DERIVED_PAYMENT_STATUS.UPCOMING_PAYMENT;
            },
            (policyInfo: CustomerPolicyInfo) => {
                return getMomentFromHippoDateString(policyInfo.effective_date).valueOf();
            },
            (policyInfo: CustomerPolicyInfo) => {
                return policyInfo.id;
            }
        ],
        ['asc', 'asc', 'asc']
    );

    return linearOrderedPolicyInfos;
}

export function getTerminatedCustomerPolicyInfos(policyInfos: Array<CustomerPolicyInfo>): Array<CustomerPolicyInfo> {
    return _.filter(policyInfos, (policyInfo: CustomerPolicyInfo) => {
        return policyInfo.status === 'terminated';
    });
}

function metEscrowCTA(policyInfo: CustomerPolicyInfo) {
    return (
        (policyInfo.payment_status === 'not_received' && policyInfo.pending_terminate) ||
        policyInfo.payment_status === 'outstanding' ||
        policyInfo.payment_status === 'due'
    );
}

function metCreditCardCTA(policyInfo: CustomerPolicyInfo) {
    return (
        policyInfo.payment_status === 'failed' ||
        policyInfo.payment_status === 'outstanding' ||
        policyInfo.payment_status === 'cc_payments_required'
    );
}

function metAchCTA(policyInfo: CustomerPolicyInfo) {
    return (
        (policyInfo.payment_status === 'failed' && policyInfo.pending_terminate) ||
        policyInfo.payment_status === 'outstanding' ||
        policyInfo.payment_status === 'not_received'
    );
}

export function shouldShowCTAModal(policyInfo: CustomerPolicyInfo) {
    return (
        (policyInfo.checkout_data.payment_method === 'escrow' && metEscrowCTA(policyInfo)) ||
        (policyInfo.checkout_data.payment_method === 'cc' && metCreditCardCTA(policyInfo)) ||
        (policyInfo.checkout_data.payment_method === 'ach' && metAchCTA(policyInfo))
    );
}

const module = angular.module('customerPortal.utilities.policyInfosHelper', []);

module.value('getActionableCustomerPolicyInfos', getActionableCustomerPolicyInfos);
module.value('getHistoricalCustomerPolicyInfos', getHistoricalCustomerPolicyInfos);
module.value('getSortedCustomerPolicyInfos', getSortedCustomerPolicyInfos);
module.value('shouldShowCTAModal', shouldShowCTAModal);
